/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Alert, IconButton, Tooltip, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import * as sysJobsApi from '../../lib/api/sysjobs';
import SysJobsBoard from '../../components/sysjobs/SysJobsBoard';
import SysJobRunConfirmDialog from '../../components/sysjobs/SysJobRunConfirmDialog';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';

const REFRESH_MS = 30000; // 요구사항: 화면이 보이는 동안 30초마다 자동 갱신
const RUN_HISTORY_SIZE = 10;

const SysJobsContainer = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const [expandedJobId, setExpandedJobId] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [runHistoryTotal, setRunHistoryTotal] = useState(0);
  const [runHistoryPage, setRunHistoryPage] = useState(0);
  const [runHistoryLoading, setRunHistoryLoading] = useState(false);

  const [confirmJob, setConfirmJob] = useState(null);
  const [runningJobId, setRunningJobId] = useState(null);

  const fetchList = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await sysJobsApi.getJobList();
      setList(sysJobsApi.extractList(res));
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        enqueueSnackbar('예약 작업 목록을 불러오지 못했습니다.', { variant: 'error' });
      }
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchList(true);

    // 탭이 백그라운드로 가면 자동 갱신을 멈춘다(EqpMonitorMngContainer와 같은 패턴).
    let timer = null;
    const start = () => { if (!timer) timer = setInterval(() => fetchList(false), REFRESH_MS); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') { fetchList(false); start(); } else { stop(); }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [fetchList]);

  const fetchRunHistory = useCallback(async (jobId, page) => {
    setRunHistoryLoading(true);
    try {
      const res = await sysJobsApi.getJobRuns(jobId, page, RUN_HISTORY_SIZE);
      setRunHistory(sysJobsApi.extractList(res));
      setRunHistoryTotal(sysJobsApi.extractTotal(res));
    } catch (e) {
      enqueueSnackbar('실행 이력을 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setRunHistoryLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleToggleExpand = (job) => {
    if (expandedJobId === job.jobId) {
      setExpandedJobId(null);
      return;
    }
    setExpandedJobId(job.jobId);
    setRunHistoryPage(0);
    fetchRunHistory(job.jobId, 0);
  };

  const handleRunHistoryPageChange = (page) => {
    setRunHistoryPage(page);
    if (expandedJobId) fetchRunHistory(expandedJobId, page);
  };

  const handleRunRequest = (job) => setConfirmJob(job);

  const handleRunConfirm = async () => {
    if (!confirmJob) return;
    const jobId = confirmJob.jobId;
    setRunningJobId(jobId);
    try {
      await sysJobsApi.runJobManually(jobId);
      enqueueSnackbar('실행을 시작했습니다.', { variant: 'success' });
      setConfirmJob(null);
      // 비동기로 돌기 시작한 참이라 바로 조회해도 RUNNING이 안 보일 수 있다 - 몇 초 뒤 한 번 더 갱신.
      fetchList(false);
      setTimeout(() => fetchList(false), 3000);
      if (expandedJobId === jobId) fetchRunHistory(jobId, 0);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.detail || '실행 요청에 실패했습니다.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setRunningJobId(null);
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ p: 3 }}>
        <BreadcrumbsContainer title="환경설정/예약 작업" />
        <Alert severity="warning">이 화면은 ksm 관리자만 볼 수 있습니다.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <BreadcrumbsContainer title="환경설정/예약 작업" />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>예약 작업</Typography>
        <Tooltip title="새로고침">
          <IconButton size="small" onClick={() => fetchList(true)}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>

      <SysJobsBoard
        list={list}
        loading={loading}
        expandedJobId={expandedJobId}
        onToggleExpand={handleToggleExpand}
        onRun={handleRunRequest}
        runningJobId={runningJobId}
        runHistory={runHistory}
        runHistoryLoading={runHistoryLoading}
        runHistoryPage={runHistoryPage}
        runHistorySize={RUN_HISTORY_SIZE}
        runHistoryTotal={runHistoryTotal}
        onRunHistoryPageChange={handleRunHistoryPageChange}
      />

      <SysJobRunConfirmDialog
        open={Boolean(confirmJob)}
        job={confirmJob}
        submitting={runningJobId === confirmJob?.jobId}
        onClose={() => setConfirmJob(null)}
        onConfirm={handleRunConfirm}
      />
    </Box>
  );
};

export default SysJobsContainer;
